import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '@/features/auth/services/authApi';

interface StrengthResult {
  score: number;       // 0–4
  label: string;
  color: string;
  checks: { label: string; passed: boolean }[];
}

function getPasswordStrength(pw: string): StrengthResult {
  const checks = [
    { label: 'At least 8 characters', passed: pw.length >= 8 },
    { label: 'Lowercase letter', passed: /[a-z]/.test(pw) },
    { label: 'Uppercase letter', passed: /[A-Z]/.test(pw) },
    { label: 'Number', passed: /\d/.test(pw) },
    { label: 'Special character (!@#$...)', passed: /[^A-Za-z0-9]/.test(pw) },
  ];
  const passed = checks.filter((c) => c.passed).length;

  if (passed <= 1) return { score: 0, label: 'Weak', color: '#ef4444', checks };
  if (passed === 2) return { score: 1, label: 'Weak', color: '#ef4444', checks };
  if (passed === 3) return { score: 2, label: 'Fair', color: '#f97316', checks };
  if (passed === 4) return { score: 3, label: 'Good', color: '#3b82f6', checks };
  return { score: 4, label: 'Strong', color: '#22c55e', checks };
}

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  const validateField = (field: string, value: string) => {
    if (field === 'email') {
      if (!value.trim()) return 'Email is required.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address.';
    }
    if (field === 'username') {
      if (!value.trim()) return 'Username is required.';
      if (value.trim().length < 3) return 'Username must be at least 3 characters.';
    }
    if (field === 'password' && !value) return 'Password is required.';
    return '';
  };

  const handleBlur = (field: string, value: string) => {
    const err = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: err }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    const eErr = validateField('email', email);
    const uErr = validateField('username', username);
    const pErr = validateField('password', password);
    if (eErr) errors.email = eErr;
    if (uErr) errors.username = uErr;
    if (pErr) errors.password = pErr;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setError('');
    if (!agreed) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      return;
    }
    if (strength.score < 2) {
      setError('Password is too weak. Please meet at least 3 of the requirements below.');
      return;
    }
    setLoading(true);
    try {
      const result = await register(email, username, password);
      navigate('/verify', { state: { email: result.email } });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-bold mb-6 text-accent">Create Account</h1>
        <hr className="mb-6" />

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-xs font-bold mb-1">Email</label>
            <input
              id="email"
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => ({ ...prev, email: '' })); }}
              onBlur={() => handleBlur('email', email)}
              required
              placeholder="you@example.com"
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? 'email-error' : 'email-hint'}
              className={`w-full px-2 py-1.5 border text-sm font-mono focus:outline-none focus:border-accent ${fieldErrors.email ? 'border-red-500' : 'border-border'}`}
            />
            {fieldErrors.email ? (
              <p id="email-error" className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
            ) : (
              <p id="email-hint" className="text-xs text-muted mt-1">We'll send a verification code to this address.</p>
            )}
          </div>
          <div>
            <label htmlFor="username" className="block text-xs font-bold mb-1">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setFieldErrors((prev) => ({ ...prev, username: '' })); }}
              onBlur={() => handleBlur('username', username)}
              required
              placeholder="your_username"
              aria-invalid={!!fieldErrors.username}
              aria-describedby={fieldErrors.username ? 'username-error' : undefined}
              className={`w-full px-2 py-1.5 border text-sm font-mono focus:outline-none focus:border-accent ${fieldErrors.username ? 'border-red-500' : 'border-border'}`}
            />
            {fieldErrors.username && <p id="username-error" className="text-xs text-red-500 mt-1">{fieldErrors.username}</p>}
          </div>
          <div>
            <label htmlFor="password" className="block text-xs font-bold mb-1">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setFieldErrors((prev) => ({ ...prev, password: '' })); }}
              onBlur={() => handleBlur('password', password)}
              required
              aria-invalid={!!fieldErrors.password}
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              className={`w-full px-2 py-1.5 border text-sm font-mono focus:outline-none focus:border-accent ${fieldErrors.password ? 'border-red-500' : 'border-border'}`}
            />
            {fieldErrors.password && <p id="password-error" className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}

            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex-1 flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full"
                        style={{
                          backgroundColor: i <= strength.score - 1
                            ? strength.color
                            : 'rgba(255,255,255,0.1)',
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-bold" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                </div>

                <ul className="space-y-0.5">
                  {strength.checks.map((c) => (
                    <li key={c.label} className="text-xs flex items-center gap-1.5">
                      <span style={{ color: c.passed ? '#22c55e' : 'var(--color-muted)' }}>
                        {c.passed ? '\u2713' : '\u2717'}
                      </span>
                      <span className={c.passed ? '' : 'text-muted'}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2">
            <input
              id="agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-accent"
            />
            <label htmlFor="agree" className="text-xs text-muted leading-relaxed">
              I agree to the{' '}
              <Link to="/terms" target="_blank" className="underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link>.
              I consent to receiving transactional emails (verification codes, account notifications)
              at the email address provided above.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !agreed}
            className="btn-terminal disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>

        <p className="text-sm text-muted mt-6">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
